import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Pause, Play } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { resolveMediaDuration } from "../../application/mediaDuration";
import { fetchMeditationAudio } from "../../application/meditationPlayback";
import { meditationBackgroundUrl } from "../../application/useMeditationAudio";
import {
    BACKGROUND_TRACK_GAIN,
    backgroundTrackSrc,
} from "../../domain/backgroundTracks";
import { BackgroundTrackId } from "../../domain/types";
import AudioPlayerBar from "./AudioPlayerBar";

type Props = {
    meditationId: string;
    /** What the player along the bottom calls it, and whose it is. */
    title?: string;
    author?: string | null;
    /** How long the recording runs, if saved; the file says once it loads. */
    durationSeconds?: number | null;
    /** The bed and the mix, so what is heard here is what a session plays. */
    backgroundTrack?: BackgroundTrackId;
    voiceVolume?: number;
    backgroundVolume?: number;
};

/**
 * Listening to a recording that is already saved.
 *
 * "This meditation has a recording" is a fact about a file; a therapist coming
 * back to one wants to know what is on it — which take they kept, whether the
 * ending is clipped, whether the bed sits right — and the only honest answer
 * is to play it.
 *
 * It plays the mix rather than the voice alone: the bed lives beside the
 * recording now, so the file on its own is not what anybody hears in session.
 * The URL is asked for on the first press rather than on render, because most
 * visits to an editor never play anything and a signed link expires.
 *
 * Once it plays, the same player the music shelves use stays along the bottom
 * of the screen — where it has got to, pause, move through it, close — so a
 * therapist reading the script further down still has the controls in view.
 */
export default function MeditationPlaybackButton({
    meditationId,
    title,
    author,
    durationSeconds,
    backgroundTrack = BackgroundTrackId.NONE,
    voiceVolume = 1,
    backgroundVolume = BACKGROUND_TRACK_GAIN,
}: Props) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [failure, setFailure] = useState<"missing" | "failed" | null>(null);
    // The player stays up from the first play until it is closed, paused or not.
    const [barOpen, setBarOpen] = useState(false);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(durationSeconds ?? 0);

    const voiceRef = useRef<HTMLAudioElement | null>(null);
    const bedRef = useRef<HTMLAudioElement | null>(null);

    const stop = () => {
        voiceRef.current?.pause();
        bedRef.current?.pause();
        setPlaying(false);
    };

    // Nothing this page started should outlive it.
    useEffect(
        () => () => {
            voiceRef.current?.pause();
            bedRef.current?.pause();
        },
        []
    );

    const toggle = async () => {
        if (playing) {
            stop();
            return;
        }
        if (voiceRef.current) {
            void voiceRef.current.play().catch(() => setPlaying(false));
            void bedRef.current?.play().catch(() => undefined);
            setPlaying(true);
            setBarOpen(true);
            return;
        }

        setLoading(true);
        setFailure(null);
        try {
            const data = await fetchMeditationAudio(meditationId);

            const voice = new Audio(URL.createObjectURL(data));
            voice.volume = voiceVolume;
            voice.onended = () => {
                stop();
                voice.currentTime = 0;
                setPosition(0);
            };
            voiceRef.current = voice;
            // Before it plays: a browser recording does not say how long it
            // is, and a bar without a length cannot show where it has got to.
            const length = await resolveMediaDuration(voice);
            if (length) setDuration(length);
            voice.ontimeupdate = () => setPosition(voice.currentTime);

            const bedSrc =
                (await meditationBackgroundUrl(meditationId)) ??
                backgroundTrackSrc(backgroundTrack);
            if (bedSrc) {
                const bed = new Audio(bedSrc);
                bed.loop = true;
                bed.volume = backgroundVolume;
                bedRef.current = bed;
            }

            await voice.play();
            void bedRef.current?.play().catch(() => undefined);
            setPlaying(true);
            setBarOpen(true);
        } catch (cause) {
            // A recording whose file is gone is a different problem from one
            // that would not play: the first is fixed by recording again, and
            // saying "could not play" would send the therapist looking for a
            // fault in their browser.
            const status = (cause as { response?: { status?: number } })?.response
                ?.status;
            setFailure(status === 404 ? "missing" : "failed");
        } finally {
            setLoading(false);
        }
    };

    const seek = (seconds: number) => {
        if (!voiceRef.current) return;
        voiceRef.current.currentTime = seconds;
        setPosition(seconds);
    };

    // Closing stops it and starts the next listen from the beginning.
    const close = () => {
        stop();
        seek(0);
        setBarOpen(false);
    };

    return (
        <span className="flex flex-wrap items-center gap-2">
            <Button
                size="sm"
                variant="outline"
                loading={loading}
                startIcon={playing ? <Pause size={15} /> : <Play size={15} />}
                onClick={() => void toggle()}
            >
                {t(playing ? "meditation.audio.stopListening" : "meditation.audio.listen")}
            </Button>
            {loading && (
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            )}
            {failure && (
                <span role="alert" className="text-xs text-status-critical">
                    {t(
                        failure === "missing"
                            ? "meditation.audio.missing"
                            : "meditation.audio.playFailed"
                    )}
                </span>
            )}
            {barOpen && (
                <AudioPlayerBar
                    title={title ?? t("meditation.player.recording")}
                    author={author}
                    paused={!playing}
                    position={position}
                    duration={duration}
                    onToggle={() => void toggle()}
                    onSeek={seek}
                    onClose={close}
                />
            )}
        </span>
    );
}
